import React from "react";
import styled, { keyframes } from "styled-components";
import { getColor } from "@/styles/ThemeConfig";

// Fallback list for a quick drop-in when the CMS has no press logos set
const FALLBACK_LOGOS = [
    {src: '/images/logos/nytimes.png', alt: 'nytimes'},
    {src: '/images/logos/forbes.png', alt: 'forbes'},
    {src: '/images/logos/travel.png', alt: 'travel'},
    {src: '/images/logos/delicious.png', alt: 'delicious'},
    {src: '/images/logos/usa.png', alt: 'usa'},
    {src: '/images/logos/telegraph.png', alt: 'telegraph'},
    {src: '/images/logos/bbc.png', alt: 'bbc'},
    {src: '/images/logos/wsj.png', alt: 'wsj'},
    {src: '/images/logos/times.png', alt: 'times'},
    {src: '/images/logos/traveler.png', alt: 'traveler'},
    {src: '/images/logos/monde.png', alt: 'monde'},
    {src: '/images/logos/nypost.png', alt: 'nypost'},
];

// A single copy narrower than the rail would leave a visible hole mid-scroll,
// so short lists get repeated until a copy is wide enough to span it.
const MIN_LOGOS_PER_COPY = 8;

// Seconds each logo takes to travel the loop. Deriving the duration from the
// logo count keeps the speed steady as logos are added/removed in the CMS.
const SECONDS_PER_LOGO = 2.5;

// The track holds two identical copies, so shifting it by exactly half its
// width lands copy B where copy A started — the loop reads as continuous.
const marquee = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const Wrap = styled.section`
  width: 100%;
  padding: 18px 0;
  background: ${(p) => p.$bg || "#f6f7f8"};
  ${(p) => (p.$borderBottom ? `border-bottom: 1px solid ${getColor('divider')};` : "")}
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
`;

const Viewport = styled.div`
  overflow: hidden;

  /* Without the animation there is nothing to carry the logos past the edge,
     so hand the rail back to the user to scroll. */
  @media (prefers-reduced-motion: reduce) {
    overflow-x: auto;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none;  /* IE 10+ */
    &::-webkit-scrollbar { display: none; }
  }
`;

const Track = styled.div`
  display: flex;
  width: max-content;
  animation: ${marquee} ${(p) => p.$duration}s linear infinite;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Group = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 0 0 auto;
  gap: ${(p) => p.$gap}px;
  /* Trailing gap so the seam between the two copies matches the spacing
     between logos inside a copy. */
  padding-right: ${(p) => p.$gap}px;

  @media (prefers-reduced-motion: reduce) {
    /* The clone only exists to make the animation loop seamlessly. */
    &[aria-hidden="true"] { display: none; }
  }
`;

const Logo = styled.img`
  height: auto;
  max-height: ${(p) => (p.$maxHeight ? `${p.$maxHeight}px` : "34px")};
  opacity: 0.9;
  filter: ${(p) => (p.$tint ? `brightness(0) saturate(100%) invert(18%) sepia(20%) saturate(400%) hue-rotate(110deg) brightness(90%)` : "none")};
  /* When a custom tint color is provided, fall back to CSS filter above for a muted mono effect;
     For true tinted assets, pass pre-tinted SVG/PNG and omit tint prop. */
  user-select: none;
  flex-shrink: 0;
`;

export default function VariantBLogosSection({
  logos = [],
  tint = null,
  bg = "#f6f7f8",
  maxHeight = 34,
  gap = 15,
  borderBottom = false,
  duration,
}) {
  const items = logos.length ? logos : FALLBACK_LOGOS;

  const copies = Math.ceil(MIN_LOGOS_PER_COPY / items.length);
  const groupItems = Array.from({ length: copies }, () => items).flat();

  const loopSeconds = duration ?? groupItems.length * SECONDS_PER_LOGO;

  // The clone is decorative: no alt text and hidden from assistive tech, so the
  // logos are announced once even though they are painted twice.
  const renderLogos = (keyPrefix, isClone) =>
    groupItems.map((l, i) => (
      <Logo
        key={`${keyPrefix}-${i}`}
        src={l.src}
        alt={isClone ? "" : l.alt || "logo"}
        width={l.width}
        height={l.height}
        $maxHeight={maxHeight}
        $tint={!!tint}
        style={tint ? { filter: `grayscale(1) contrast(1.05) opacity(0.9)` } : undefined}
      />
    ));

  return (
    <Wrap $bg={bg} $borderBottom={borderBottom}>
      <Inner>
        <Viewport>
          <Track $duration={loopSeconds}>
            <Group $gap={gap}>{renderLogos("a", false)}</Group>
            <Group $gap={gap} aria-hidden="true">{renderLogos("b", true)}</Group>
          </Track>
        </Viewport>
      </Inner>
    </Wrap>
  );
}
